import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Mail, Phone, Instagram, MapPin, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";

export default function Contact() {
  const { settings } = useOutletContext() || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/contact", form); setDone(true); }
    catch (err) { toast.error(formatErr(err)); }
    finally { setLoading(false); }
  };

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-16" data-testid="contact-page">
      <div className="text-center max-w-2xl mx-auto">
        <div className="text-xs tracking-[0.25em] uppercase text-larc-gold">— İletişim</div>
        <h1 className="font-heading text-5xl md:text-6xl tracking-tight mt-3">Bizimle iletişime geçin</h1>
        <p className="text-larc-muted mt-4">Sorularınızı, özel taleplerinizi ve toptan siparişler için bize ulaşın.</p>
      </div>

      <div className="mt-14 grid md:grid-cols-2 gap-10">
        <div className="bg-white border border-larc-border rounded-2xl p-8 md:p-10 space-y-6">
          <ContactItem icon={<Mail size={16} />} label="E-posta" value={settings?.contact_email || "hello@larcraft.shop"} href={`mailto:${settings?.contact_email || ""}`} />
          <ContactItem icon={<Phone size={16} />} label="Telefon" value={settings?.phone || ""} href={`tel:${settings?.phone || ""}`} />
          <ContactItem icon={<MessageCircle size={16} />} label="WhatsApp" value={settings?.whatsapp || ""} href={`https://wa.me/${(settings?.whatsapp || "").replace(/[^0-9]/g, "")}`} />
          <ContactItem icon={<Instagram size={16} />} label="Instagram" value={(settings?.instagram_url || "").replace("https://", "")} href={settings?.instagram_url} />
          <ContactItem icon={<MapPin size={16} />} label="Adres" value={settings?.address || ""} />
          <div className="border-t border-larc-border pt-6 text-xs uppercase tracking-[0.18em] text-larc-muted">{settings?.working_hours}</div>
        </div>

        <form onSubmit={submit} className="bg-white border border-larc-border rounded-2xl p-8 md:p-10 space-y-5" data-testid="contact-form">
          {done ? (
            <div className="text-center py-10" data-testid="contact-success">
              <h3 className="font-heading text-3xl">Mesajınız iletildi</h3>
              <p className="text-larc-muted mt-3">Size en kısa sürede dönüş yapacağız.</p>
            </div>
          ) : (
            <>
              <Field label="Ad Soyad" required value={form.name} onChange={handle("name")} testid="contact-name" />
              <Field label="E-posta" required type="email" value={form.email} onChange={handle("email")} testid="contact-email" />
              <Field label="Telefon" value={form.phone} onChange={handle("phone")} testid="contact-phone" />
              <Field label="Konu" value={form.subject} onChange={handle("subject")} testid="contact-subject" />
              <Field label="Mesajınız" required value={form.message} onChange={handle("message")} testid="contact-message" textarea />
              <button type="submit" disabled={loading} className="inline-flex items-center gap-2 bg-larc-ink text-white rounded-full px-8 py-3.5 text-sm hover:bg-larc-gold transition-colors disabled:opacity-50" data-testid="contact-submit">
                {loading ? "Gönderiliyor…" : "Gönder"} <Send size={14} />
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
}

function ContactItem({ icon, label, value, href }) {
  const content = (
    <div className="flex items-start gap-4">
      <div className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-larc-border text-larc-gold">{icon}</div>
      <div>
        <div className="text-xs uppercase tracking-[0.15em] text-larc-muted">{label}</div>
        <div className="text-larc-ink mt-1">{value || "—"}</div>
      </div>
    </div>
  );
  return href ? <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="block hover:opacity-80">{content}</a> : content;
}

function Field({ label, required, type = "text", value, onChange, testid, textarea }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.15em] text-larc-muted">{label}{required && " *"}</span>
      {textarea ? (
        <textarea required={required} value={value} onChange={onChange} rows={4} className="mt-2 w-full bg-white border border-larc-border rounded-lg px-4 py-3 outline-none focus:border-larc-gold text-sm" data-testid={testid} />
      ) : (
        <input required={required} type={type} value={value} onChange={onChange} className="mt-2 w-full bg-white border border-larc-border rounded-lg px-4 py-3 outline-none focus:border-larc-gold text-sm" data-testid={testid} />
      )}
    </label>
  );
}
